import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ART_INDEX_PATH = '/art';
const FASHION_INDEX_PATH = '/fashion';
const DEFAULT_FASHION_SHOW = 'EvacuateFromMeaning';

function isArtDetailPath(pathname) {
  return /^\/art\/[^/]+\/?$/.test(pathname);
}

function isFashionDetailPath(pathname) {
  return /^\/fashion\/[^/]+\/[^/]+\/?$/.test(pathname);
}

function getFashionShow(search) {
  return new URLSearchParams(search).get('look') || DEFAULT_FASHION_SHOW;
}

function getScrollTop() {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

function setScrollTop(top) {
  const html = document.documentElement;
  const body = document.body;
  const scrollingElement = document.scrollingElement || html;

  window.scrollTo(0, top);
  scrollingElement.scrollTop = top;
  html.scrollTop = top;
  body.scrollTop = top;
}

function runWithoutSmoothScroll(callback) {
  const html = document.documentElement;
  const body = document.body;
  const previousHtmlScrollBehavior = html.style.scrollBehavior;
  const previousBodyScrollBehavior = body.style.scrollBehavior;
  let restored = false;

  const restore = () => {
    if (restored) {
      return;
    }

    restored = true;
    html.style.scrollBehavior = previousHtmlScrollBehavior;
    body.style.scrollBehavior = previousBodyScrollBehavior;
  };

  html.style.scrollBehavior = 'auto';
  body.style.scrollBehavior = 'auto';
  callback();

  const restoreTimerId = window.setTimeout(restore, 120);

  return () => {
    window.clearTimeout(restoreTimerId);
    restore();
  };
}

export default function ArtScrollManager() {
  const { pathname, search } = useLocation();
  const artIndexScrollTopRef = useRef(0);
  const fashionScrollTopByShowRef = useRef({});

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    const isArtIndex = pathname === ART_INDEX_PATH;
    const isArtDetail = isArtDetailPath(pathname);
    const isFashionIndex = pathname === FASHION_INDEX_PATH;
    const isFashionDetail = isFashionDetailPath(pathname);
    const fashionScrollTopByShow = fashionScrollTopByShowRef.current;
    let restoreScrollBehavior = () => {};
    let frameId;
    let timeoutId;

    if (isArtIndex || isArtDetail || isFashionIndex || isFashionDetail) {
      let nextScrollTop = 0;

      if (isArtIndex) {
        nextScrollTop = artIndexScrollTopRef.current;
      }

      if (isFashionIndex) {
        nextScrollTop = fashionScrollTopByShow[getFashionShow(search)] || 0;
      }

      restoreScrollBehavior = runWithoutSmoothScroll(() => {
        setScrollTop(nextScrollTop);
        frameId = window.requestAnimationFrame(() => setScrollTop(nextScrollTop));
        timeoutId = window.setTimeout(() => setScrollTop(nextScrollTop), 80);
      });
    }

    return () => {
      if (pathname === ART_INDEX_PATH) {
        artIndexScrollTopRef.current = getScrollTop();
      }

      if (pathname === FASHION_INDEX_PATH) {
        fashionScrollTopByShow[getFashionShow(search)] = getScrollTop();
      }

      restoreScrollBehavior();
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [pathname, search]);

  return null;
}
